import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, TrendingDown, CheckCircle2, XCircle } from 'lucide-react';
import { getAuthToken } from '../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCES = [
  { id: 'all', label: 'All Sources' },
  { id: 'google_ads', label: 'Google Ads' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'direct', label: 'Direct' },
  { id: 'organic', label: 'Organic' },
  { id: 'other', label: 'Other' },
];
const DAY_OPTIONS = [7, 30, 90];

const SOURCE_BADGE = {
  google_ads: 'bg-blue-100 text-blue-700',
  facebook: 'bg-indigo-100 text-indigo-700',
  direct: 'bg-gray-100 text-gray-700',
  organic: 'bg-emerald-100 text-emerald-700',
  other: 'bg-amber-100 text-amber-700',
};

export const RigReadyFunnelTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [source, setSource] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/rigready-funnel`, {
        params: { days, source },
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      setData(res.data);
    } catch {
      toast.error('Failed to load Rig Ready funnel analytics');
    } finally {
      setLoading(false);
    }
  }, [days, source]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading && !data) {
    return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#004E86]" size={32} /></div>;
  }
  if (!data) return null;

  const s = data.stages || {};
  const c = data.conversion || {};
  const stages = [
    { label: 'Rig Ready Visits', value: s.rigready_visits, pct: null },
    { label: 'TowIQ Starts', value: s.towiq_starts, pct: c.visit_to_towiq_start },
    { label: 'TowIQ Results', value: s.towiq_results, pct: c.towiq_start_to_result },
    { label: 'RV Match Starts', value: s.rvmatch_starts, pct: c.result_to_rvmatch_start },
    { label: 'Leads Submitted', value: s.leads_submitted, pct: c.result_to_lead, pctLabel: 'of results' },
  ];

  return (
    <div data-testid="rigready-funnel-tab">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          {DAY_OPTIONS.map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-sm font-semibold ${days === d ? 'bg-[#004E86] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              data-testid={`funnel-days-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {SOURCES.map((so) => (
            <button
              key={so.id}
              onClick={() => setSource(so.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border ${source === so.id ? 'bg-[#004E86] text-white border-[#004E86]' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
              data-testid={`funnel-source-${so.id}`}
            >
              {so.label}{data.source_breakdown?.[so.id] != null && so.id !== 'all' ? ` (${data.source_breakdown[so.id]})` : ''}
            </button>
          ))}
        </div>
        {loading && <Loader2 className="animate-spin text-gray-400" size={16} />}
      </div>

      {/* Funnel stages */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {stages.map((st) => (
          <div key={st.label} className="bg-white rounded-lg shadow-md p-4" data-testid={`funnel-stage-${st.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <p className="text-[11px] font-bold tracking-wide text-gray-500 uppercase">{st.label}</p>
            <p className="text-3xl font-black text-slate-900 mt-1">{st.value ?? 0}</p>
            {st.pct != null && (
              <p className="text-xs font-semibold text-[#004E86] mt-1 flex items-center gap-1">
                <TrendingDown size={12} /> {st.pct}% {st.pctLabel || 'of prev.'}
              </p>
            )}
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500 mb-6">
        Overall visit → lead: <span className="font-bold text-slate-800">{c.visit_to_lead ?? 0}%</span>.
        Anonymous funnel activity only — these are not leads and never enter the sales queue.
      </p>

      {/* Recent anonymous sessions */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="font-bold text-slate-900 text-sm">Recent Anonymous TowIQ Sessions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['When', 'Source', 'Vehicle', 'Tow Result', 'Confidence', 'RV Match', 'Lead'].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data.recent_sessions || []).length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">No anonymous sessions in this window yet.</td></tr>
              )}
              {(data.recent_sessions || []).map((r, i) => (
                <tr key={`${r.anon_id}-${i}`} data-testid={`funnel-session-row-${i}`}>
                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${SOURCE_BADGE[r.source] || SOURCE_BADGE.other}`}>
                      {SOURCES.find((so) => so.id === r.source)?.label || r.source || '—'}
                    </span>
                    {r.campaign && <span className="ml-1.5 text-[11px] text-gray-400">{r.campaign}</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-800 font-medium">{r.vehicle || '—'}{r.vin_used && <span className="ml-1 text-[10px] font-bold text-blue-600">VIN</span>}</td>
                  <td className="px-3 py-2 text-slate-800">{r.tow_capacity_lbs ? `${r.tow_capacity_lbs.toLocaleString()} lbs` : '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{r.source_state || r.confidence || '—'}</td>
                  <td className="px-3 py-2">{r.continued_to_rvmatch ? <CheckCircle2 className="text-emerald-500" size={16} /> : <XCircle className="text-gray-300" size={16} />}</td>
                  <td className="px-3 py-2">{r.became_lead ? <CheckCircle2 className="text-emerald-600" size={16} /> : <XCircle className="text-gray-300" size={16} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RigReadyFunnelTab;
